import { LineProps } from "recharts";
import { getBenchmarkColor } from './benchmarkUtils';

/**
 * Enhances chart rendering configuration to include benchmark data
 */
export const enhanceChartConfigWithBenchmark = (
    originalConfig: any,
    benchmarkData: any,
    showBenchmark: boolean,
    getMetricInfo: (key: string) => any,
    mode: 'dark' | 'light' = 'dark'
) => {
    if (!showBenchmark || !benchmarkData?.datasets) {
        return originalConfig;
    }

    // Clone the original config to avoid mutation
    const enhancedConfig = {
        ...originalConfig,
        chartElementsConfig: {
            ...originalConfig.chartElementsConfig,
            lines: [...originalConfig.chartElementsConfig.lines]
        }
    };

    // Add benchmark lines
    benchmarkData.datasets.forEach((dataset: any) => {
        const originalMetricKey = dataset.id.replace('_benchmark', '');
        const metricInfo = getMetricInfo(originalMetricKey);
        
        // Get the original metric's color and create a benchmark version
        const originalLine = originalConfig.chartElementsConfig.lines.find(
            (line: LineProps) => line.dataKey === originalMetricKey
        );
        const originalColor = originalLine?.stroke || metricInfo.color;
        const benchmarkColor = getBenchmarkColor(originalColor);

        enhancedConfig.chartElementsConfig.lines.push({
            key: dataset.id,
            type: "monotone",
            dataKey: dataset.id,
            stroke: benchmarkColor,
            strokeWidth: 2,
            strokeDasharray: "8 4", // Dashed line for benchmark
            name: dataset.label,
            yAxisId: originalLine?.yAxisId || 'default',
            connectNulls: true,
            dot: false,
            activeDot: { 
                r: 3, 
                fill: benchmarkColor,
                stroke: benchmarkColor,
                strokeWidth: 1
            },
            // Custom properties to identify benchmark data
            isBenchmark: true,
            originalMetric: originalMetricKey
        });
    });

    // Update available metrics to include benchmark metrics
    const benchmarkMetrics = benchmarkData.datasets.map((dataset: any) => dataset.id);
    enhancedConfig.availableMetrics = [
        ...originalConfig.availableMetrics,
        ...benchmarkMetrics
    ];

    return enhancedConfig;
};

/**
 * Processes benchmark data and transforms it for chart display
 * Uses the same transformation logic as regular data
 */
export const transformBenchmarkDataForChart = (benchmarkData: any) => {
    if (!benchmarkData?.datasets || !Array.isArray(benchmarkData.datasets)) {
        return [];
    }

    const timestampMap = new Map();

    // Process benchmark datasets similarly to regular data transformation
    benchmarkData.datasets.forEach((dataset: any) => {
        if (dataset.data && Array.isArray(dataset.data)) {
            dataset.data.forEach((point: any) => {
                const timestampValue = new Date(point.timestamp).getTime();
                if (!timestampMap.has(timestampValue)) {
                    timestampMap.set(timestampValue, { timestamp: timestampValue });
                }
                // Use the benchmark ID (which includes _benchmark suffix)
                timestampMap.get(timestampValue)[dataset.id] = point.value;
            });
        }
    });

    return Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Merges chart data with benchmark data
 */
export const mergeChartDataWithBenchmark = (
    regularChartData: any[],
    benchmarkChartData: any[]
): any[] => {
    if (!benchmarkChartData.length) {
        return regularChartData;
    }

    const timestampMap = new Map();

    // Add regular data
    regularChartData.forEach(dataPoint => {
        timestampMap.set(dataPoint.timestamp, { ...dataPoint });
    });

    // Identify all benchmark keys
    const benchmarkKeys = new Set<string>();

    // Merge benchmark data
    benchmarkChartData.forEach(benchmarkPoint => {
        const existing = timestampMap.get(benchmarkPoint.timestamp);
        
        // Collect keys
        Object.keys(benchmarkPoint).forEach(key => {
            if (key !== 'timestamp') {
                benchmarkKeys.add(key);
            }
        });

        if (existing) {
            // Merge benchmark values into existing data point
            Object.keys(benchmarkPoint).forEach(key => {
                if (key !== 'timestamp') {
                    existing[key] = benchmarkPoint[key];
                }
            });
        } else {
            // Create new data point with benchmark data
            timestampMap.set(benchmarkPoint.timestamp, { ...benchmarkPoint });
        }
    });

    const mergedData = Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp);

    // Interpolate missing benchmark values
    benchmarkKeys.forEach(key => {
        interpolateValues(mergedData, key);
    });

    return mergedData;
};

const interpolateValues = (data: any[], key: string) => {
    const knownIndices = data.map((point, index) => ({ hasValue: point[key] !== undefined && point[key] !== null, index }))
                             .filter(item => item.hasValue)
                             .map(item => item.index);

    if (knownIndices.length < 2) return;

    for (let i = 0; i < knownIndices.length - 1; i++) {
        const startIndex = knownIndices[i];
        const endIndex = knownIndices[i+1];
        
        const startPoint = data[startIndex];
        const endPoint = data[endIndex];
        
        const startVal = startPoint[key];
        const endVal = endPoint[key];
        const startTime = startPoint.timestamp;
        const endTime = endPoint.timestamp;

        for (let j = startIndex + 1; j < endIndex; j++) {
            const currentPoint = data[j];
            const currentTime = currentPoint.timestamp;
            
            const ratio = (currentTime - startTime) / (endTime - startTime);
            const interpolatedVal = startVal + (endVal - startVal) * ratio;
            
            currentPoint[key] = interpolatedVal;
        }
    }
};

/**
 * Filters benchmark metrics from the chart config for legend display
 */
export const getBenchmarkMetricsForLegend = (chartConfig: any): string[] => {
    if (!chartConfig?.chartElementsConfig?.lines) {
        return [];
    }

    return chartConfig.chartElementsConfig.lines
        .filter((line: any) => line.isBenchmark)
        .map((line: any) => line.dataKey);
};
